import {
  LocalityAddress,
  PostalAddress,
  YesNoAnswer,
  YesNoDontKnowAnswer,
} from 'scenes/common/types';
import {
  HistoryDetail,
  PostalAddressOption,
  TermsAndConditions,
} from 'scenes/common/flows/policyDetails/types';
import { PolicyHolder } from 'scenes/common/flows/addPolicyHolder/types';
import { HomeFunnelState } from 'scenes/home/flows/types';
import { defaultPolicyHolder } from 'scenes/home/flows/policyDetails/defaults';

export const getPropertyInGoodCondition = (
  state: HomeFunnelState
): YesNoAnswer => state.policyDetails.propertyInGoodCondition;

export const isPropertyInGoodCondition = (state: HomeFunnelState): boolean =>
  getPropertyInGoodCondition(state) === YesNoAnswer.Yes;

export const heritageListedAnswerRequired = (buildYear: string) =>
  ['Victorian', 'Fedr'].includes(buildYear);

export const getHeritageListed = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.heritageListed;

export const isHeritageListed = (state: HomeFunnelState): boolean =>
  getHeritageListed(state) === YesNoAnswer.Yes;

export const rewiredOrReplumbedAnswerRequired = (buildYear: string) =>
  ['Victorian', 'Fedr'].includes(buildYear);

export const getRewiredOrReplumbed = (
  state: HomeFunnelState
): YesNoDontKnowAnswer => state.policyDetails.rewiredOrReplumbed;

export const hasBeenRewiredOrReplumbed = (state: HomeFunnelState): boolean =>
  getRewiredOrReplumbed(state) === YesNoDontKnowAnswer.Yes;

export const getPropertyUnoccupied = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.propertyUnoccupied;

export const isPropertyUnoccupied = (state: HomeFunnelState): boolean =>
  getPropertyUnoccupied(state) === YesNoAnswer.Yes;

export const getPropertyRenovationCurrent = (
  state: HomeFunnelState
): YesNoAnswer => state.policyDetails.propertyRenovationCurrent;

export const isPropertyUnderRenovation = (state: HomeFunnelState): boolean =>
  getPropertyRenovationCurrent(state) === YesNoAnswer.Yes;

export const getPropertyRenovationFuture = (
  state: HomeFunnelState
): YesNoAnswer =>
  state.policyDetails.propertyRenovationFuture || YesNoAnswer.Unspecified;

export const anyFutureRenovationPlanned = (state: HomeFunnelState): boolean =>
  getPropertyRenovationFuture(state) === YesNoAnswer.Yes;

export const getOver5Acres = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.over5Acres;

export const isOver5Acres = (state: HomeFunnelState): boolean =>
  getOver5Acres(state) === YesNoAnswer.Yes;

export const getPropertyOccupancy = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.propertyOccupancy;

export const hasMoreThan5UnrelatedPeople = (state: HomeFunnelState): boolean =>
  getPropertyOccupancy(state) === YesNoAnswer.Yes;

export const getBusinessName = (state: HomeFunnelState) =>
  state.policyDetails.businessName;

export const getBusinessAbn = (state: HomeFunnelState) =>
  state.policyDetails.abn;

export const getPersonalContactFirstName = (state: HomeFunnelState) =>
  state.policyDetails.personalContactFirstName;

export const getPersonalContactLastName = (state: HomeFunnelState) =>
  state.policyDetails.personalContactLastName;

export const getBusinessContactFirstName = (state: HomeFunnelState) =>
  state.policyDetails.businessContactFirstName;

export const getBusinessContactLastName = (state: HomeFunnelState) =>
  state.policyDetails.businessContactLastName;

export const getContactEmailAddress = (state: HomeFunnelState): string =>
  state.policyDetails.contactEmailAddress ?? '';

export const getContactPhoneNumber = (state: HomeFunnelState): string =>
  state.policyDetails.contactPhoneNumber ?? '';

export const getAddAdditionalPolicyHolder = (
  state: HomeFunnelState
): YesNoAnswer => state.policyDetails.addAdditionalPolicyHolder;

export const getAdditionalPolicyHolders = (
  state: HomeFunnelState
): PolicyHolder[] => state.policyDetails.additionalPolicyHolders;

export const getAdditionalPolicyHolder = (
  state: HomeFunnelState
): PolicyHolder =>
  state.policyDetails.additionalPolicyHolder || defaultPolicyHolder;

export const hasAdditionalPolicyHolders = (state: HomeFunnelState) =>
  getAdditionalPolicyHolders(state).length > 0;

export const getHistoryDetails = (state: HomeFunnelState): HistoryDetail[] =>
  state.policyDetails.historyDetails;

export const getMortgageProvider = (state: HomeFunnelState): string =>
  state.policyDetails.mortgageProvider;

export const getMortgageProviderOther = (state: HomeFunnelState): string =>
  state.policyDetails.mortgageProviderOther;

export const getPostalAddressOption = (
  state: HomeFunnelState
): PostalAddressOption => state.policyDetails.postalAddressOption;

export const getPostalAddress = (state: HomeFunnelState): PostalAddress =>
  state.policyDetails.postalAddress;

export const getLockedBoxAddress = (state: HomeFunnelState): LocalityAddress =>
  state.policyDetails.lockedBoxAddress;

export const isPostalAddressLockedBagGpo = (state: HomeFunnelState): boolean =>
  state.policyDetails.postalAddressOption ===
  PostalAddressOption.LockedBagGpoAddress;

export const isPostalAddressDifferentManual = (
  state: HomeFunnelState
): boolean => state.policyDetails.isPostalAddressDifferentManual ?? false;

export const sameRiskAndPostalAddress = (state: HomeFunnelState): boolean =>
  state.policyDetails.postalAddressOption === PostalAddressOption.MainAddress;

export const getTermsAndConditions = (
  state: HomeFunnelState
): TermsAndConditions => state.policyDetails.termsAndCondition;

export const hasAgreedTermsAndConditions = (state: HomeFunnelState): boolean =>
  getTermsAndConditions(state) === TermsAndConditions.Accepted;

export const getCashKeptAtHome = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.cashKeptAtHome;

export const isCashKeptAtHome = (state: HomeFunnelState): boolean =>
  getCashKeptAtHome(state) === YesNoAnswer.Yes;

export const getStockKeptAtHome = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.stockKeptAtHome;

export const isStockKeptAtHome = (state: HomeFunnelState): boolean =>
  getStockKeptAtHome(state) === YesNoAnswer.Yes;

export const getHazardGoodsKeptAtHome = (state: HomeFunnelState): YesNoAnswer =>
  state.policyDetails.hazardGoodsKeptAtHome;

export const areHazardGoodsKeptAtHome = (state: HomeFunnelState): boolean =>
  getHazardGoodsKeptAtHome(state) === YesNoAnswer.Yes;

export const getNumberOfVisits = (state: HomeFunnelState): number =>
  state.policyDetails.numberOfVisits;

export const getBusinessRegisteredForGst = (
  state: HomeFunnelState
): YesNoAnswer => state.policyDetails.businessRegisteredForGst;

export const isBusinessRegisteredForGst = (state: HomeFunnelState): boolean =>
  getBusinessRegisteredForGst(state) === YesNoAnswer.Yes;

export const getMissingPolicyStartDate = (state: HomeFunnelState): string =>
  state.reviewDetails.policyStartDate;
